import React, { useMemo, useState } from "react";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { User, MessageSquare, Edit, FileText, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { slugify } from "@/utils/slug";
import ShareDialog from "./ShareDialog";
import { useSavedPosit } from "@/hooks/useSavedPosit";

// Utility function to truncate text with character limit
const truncateText = (text: string, maxLength: number = 120): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + "...";
};

interface PositListCardProps {
  posit: {
    id: string;
    title: string;
    public_description?: string | null;
    posit_description?: string | null;
    subscription_price?: number | null;
    is_monetized?: boolean;
  };
  creatorHandle?: string | null;
  creatorName?: string | null;
  showEditButton?: boolean;
  showCreatorButton?: boolean;
  showShareButton?: boolean;
  isPremium?: boolean;
  variant?: 'default' | 'compact';
  showWikiButton?: boolean;
}

const PositListCard: React.FC<PositListCardProps> = ({
  posit,
  creatorHandle,
  creatorName,
  showEditButton = false,
  showCreatorButton = true,
  showShareButton = true,
  isPremium = false,
  variant = 'default',
  showWikiButton = true,
}) => {
  const navigate = useNavigate();
  const { isSaved, toggle, isLoading } = useSavedPosit(posit.id);
  
  const isPaid = posit.is_monetized && posit.subscription_price && posit.subscription_price > 0;
  const priceShort = isPaid ? `$${posit.subscription_price}` : null;
  const priceMonthly = isPaid ? `$${posit.subscription_price}/m` : null;
  const description = posit.public_description || posit.posit_description;

  // Determine if description exceeds our preferred preview length
  const previewLimit = 320;
  const isLongDescription = useMemo(() => {
    return !!description && description.length > previewLimit;
  }, [description]);
  const [isExpanded, setIsExpanded] = useState(false);

  // Helper to render action buttons
  const renderActions = (stackVertical: boolean) => (
    <div className={`flex ${stackVertical ? 'flex-col' : 'flex-row'} items-center ${stackVertical ? 'gap-2' : 'gap-1.5'}`}>
      <Button
        variant="ghost"
        size="sm"
        className={`w-9 p-0 ${isSaved ? 'text-blue-500' : 'text-muted-foreground'}`}
        onClick={toggle}
        title={isSaved ? 'Unsave' : 'Save'}
        disabled={isLoading}
      >
        <Star className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
      </Button>
      {showCreatorButton && (
        <Button
          variant="outline"
          size="sm"
          className="w-9 p-0"
          disabled={!creatorHandle}
          onClick={() => creatorHandle && navigate(`/p/${creatorHandle}`)}
        >
          <User className="w-3 h-3" />
        </Button>
      )}
      {showShareButton && (
        <ShareDialog 
          positId={posit.id} 
          isPremium={isPremium}
          buttonClassName="w-9 p-0"
          buttonSize="sm"
          showLabel={false}
        />
      )}
      {showEditButton && (
        <Button
          variant="outline"
          size="sm"
          className="w-9 p-0"
          onClick={() => navigate(`/update-posit/${posit.id}`)}
        >
          <Edit className="w-3 h-3" />
        </Button>
      )}
      {showWikiButton && (
        <Button
          onClick={() => navigate(`/d/${posit.id}/${slugify(posit.title)}`)}
          size="sm"
          className="w-9 p-0"
          title="Details"
        >
          <FileText className="w-3 h-3" />
        </Button>
      )}
      <Button
        onClick={() => navigate(`/chat/${posit.id}`)}
        size="sm"
        className="w-9 p-0"
        title="Chat"
      >
        <MessageSquare className="w-3 h-3" />
      </Button>
    </div>
  );

  const compactLayout = (
    <Card className="relative hover:shadow-lg transition-shadow group">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center gap-4">
          {/* Left: Title + description */}
          <div className="flex-1 min-w-0 pr-2 sm:pr-0">
            <div className="flex items-center gap-2 min-w-0">
              <CardTitle className="text-base font-semibold leading-tight whitespace-normal break-words line-clamp-2">
                {posit.title}
              </CardTitle>
              {priceMonthly && (
                <span className="flex-shrink-0 text-[10px] sm:text-xs font-medium px-2 py-0.5 rounded bg-muted/30 text-muted-foreground">
                  {priceMonthly}
                </span>
              )}
            </div>
            {description && (
              <div className="mt-1">
                <p className={`text-sm text-muted-foreground leading-snug ${!isExpanded ? 'line-clamp-4 sm:line-clamp-3' : ''}`}>
                  {isExpanded ? description : truncateText(description, previewLimit)}
                </p>
                {isLongDescription && (
                  <button
                    type="button"
                    onClick={() => setIsExpanded((v) => !v)}
                    className="mt-1 text-xs text-primary hover:underline"
                  >
                    {isExpanded ? 'Less' : 'More'}
                  </button>
                )}
                {/* Meta row: creator */}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {creatorHandle && (
                    <button
                      type="button"
                      onClick={() => navigate(`/p/${creatorHandle}`)}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline min-w-0 max-w-[85%] sm:max-w-none whitespace-nowrap overflow-hidden text-ellipsis"
                      aria-label={`View ${creatorName || creatorHandle}'s profile`}
                    >
                      <User className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{creatorName || creatorHandle}</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* Right: Actions column */}
          <div className="flex flex-col items-end justify-start gap-2 flex-shrink-0 w-[78px] sm:w-[88px]">
            {/* Horizontal row for actions to avoid vertical stacking feel */}
            <div className="flex items-center gap-1.5">
              {renderActions(false)}
            </div>
          </div>
        </div>
      </CardHeader>
    </Card>
  );

  const defaultLayout = (
    <Card className="relative hover:shadow-lg transition-shadow group">
      <CardHeader className={variant === 'compact' ? 'pb-2' : 'pb-3'}>
        <div className="space-y-3">
          <div className="flex justify-between items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <CardTitle className={`${variant === 'compact' ? 'text-base' : 'text-lg'} font-semibold leading-tight whitespace-normal break-words ${variant === 'compact' ? 'line-clamp-2' : ''}`}>
                  {posit.title}
                </CardTitle>
                {priceMonthly && (
                  <span className="flex-shrink-0 text-[10px] sm:text-xs font-medium px-2 py-0.5 rounded bg-muted/30 text-muted-foreground">
                    {priceMonthly}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              {renderActions(false)}
            </div>
          </div>
          {description && (
            <div className="w-full">
              <p className={`text-sm text-muted-foreground ${variant === 'compact' ? 'leading-snug' : 'leading-relaxed'}`}>
                {truncateText(description, 120)}
              </p>
            </div>
          )}
        </div>
      </CardHeader>
    </Card>
  );

  return variant === 'compact' ? compactLayout : defaultLayout;
};

export default PositListCard; 